// Refactored ListData component (MUI removed, Tailwind used)
'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ChevronDown } from 'lucide-react';

interface Item {
  id: number;
  country_name?: string;
  state_name?: string;
  city_name?: string;
  country_url?: string;
  state_url?: string;
  city_url?: string;
}

const RedirectSectionItem = ({ item, type }: { item: Item; type: string }) => {
  const getRedirectUrl = () => {
    switch (type) {
      case 'Study Abroad':
        return item?.country_url ? item.country_url.replace('study-in-', 'colleges-in-') : '#';
      case 'States':
        return item?.state_url ? `colleges-in-${item.state_url}` : '#';
      case 'Top Cities':
        return item?.city_url ? `colleges-in-${item.city_url}` : '#';
      default:
        return '#';
    }
  };

  let displayName = type === 'Study Abroad' ? item.country_name : type === 'States' ? item.state_name : item.city_name;

  if (!displayName) return null;

  return (
    <Link href={`/${getRedirectUrl()}`}>
      <span className="line-clamp-2 text-sm text-white font-light">{displayName}</span>
    </Link>
  );
};

const CollapsibleSection = ({
  title,
  children,
  isOpen,
  toggle,
}: {
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  toggle: () => void;
}) => (
  <div className="flex flex-col gap-1 text-xs font-light">
    <button onClick={toggle} className="flex px-2 py-1 cursor-pointer items-center gap-x-2  ">
      <p className="text-white text-base">{title}</p>
      <ChevronDown className={`size-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    {isOpen && <div className="flex flex-col px-2 py-1 gap-2">{children}</div>}
  </div>
);

const ListData = ({ topClg, streams, cities, states, countries }: Record<string, any[]>) => {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    topClg: false,
    streams: false,
    cities: false,
    states: false,
    countries: false,
  });

  const toggleSection = (key: string) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 xl:gap-x-6 gap-x-2 md:mt-8 md:border-none">
      {/* Desktop view */}
      {[
        {
          key: 'topClg',
          label: 'Top Colleges',
          data: topClg,
          render: (d: any) => (
            <Link
              key={d.id}
              href={d.is_online_college ? `/colleges/${d.college_url}` : `/colleges/${d.college_url}/college-info`}
            >
              <span className="line-clamp-2 text-sm text-white font-light">{d.abbreviation}</span>
            </Link>
          ),
        },
        {
          key: 'streams',
          label: 'Top Universities',
          data: streams,
          render: (d: any) => (
            <Link key={d.id} href={`/${d.stream_name.toLowerCase().replace(/\s+/g, '-')}-colleges-in-india`}>
              <span className="line-clamp-2 text-sm text-white font-light">{d.stream_name}</span>
            </Link>
          ),
        },
        {
          key: 'cities',
          label: 'Top Cities',
          data: cities,
          render: (d: any) => <RedirectSectionItem key={d.id} item={d} type="Top Cities" />,
        },
        {
          key: 'states',
          label: 'States',
          data: states,
          render: (d: any) => <RedirectSectionItem key={d.id} item={d} type="States" />,
        },
        {
          key: 'countries',
          label: 'Study Abroad',
          data: countries,
          render: (d: any) => <RedirectSectionItem key={d.id} item={d} type="Study Abroad" />,
        },
      ].map(
        ({ key, label, data, render }) =>
          data?.length > 0 && (
            <div key={key} className="hidden md:flex flex-col gap-2.5 font-light">
              <div className="font-semibold text-base">{label}</div>
              {data.map(render)}
            </div>
          ),
      )}

      {/* Mobile collapsible */}
      <div className="grid sm:grid-cols-3 grid-cols-1 md:hidden gap-2 mt-4">
        {[
          {
            key: 'topClg',
            label: 'Top Colleges',
            data: topClg,
            render: (d: any) => (
              <Link
                key={d.id}
                href={d.is_online_college ? `/colleges/${d.college_url}` : `/colleges/${d.college_url}/college-info`}
              >
                <span className="line-clamp-2 text-sm">{d.abbreviation}</span>
              </Link>
            ),
          },
          {
            key: 'streams',
            label: 'Top Universities',
            data: streams,
            render: (d: any) => (
              <Link key={d.id} href={`/${d.stream_name.toLowerCase().replace(/\s+/g, '-')}-colleges-in-india`}>
                <span className="line-clamp-2 text-sm">{d.stream_name}</span>
              </Link>
            ),
          },
          {
            key: 'cities',
            label: 'Top Cities',
            data: cities,
            render: (d: any) => <RedirectSectionItem key={d.id} item={d} type="Top Cities" />,
          },
          {
            key: 'states',
            label: 'States',
            data: states,
            render: (d: any) => <RedirectSectionItem key={d.id} item={d} type="States" />,
          },
          {
            key: 'countries',
            label: 'Study Abroad',
            data: countries,
            render: (d: any) => <RedirectSectionItem key={d.id} item={d} type="Study Abroad" />,
          },
        ].map(
          ({ key, label, data, render }) =>
            data?.length > 0 && (
              <CollapsibleSection key={key} title={label} isOpen={openSections[key]} toggle={() => toggleSection(key)}>
                {data.map(render)}
              </CollapsibleSection>
            ),
        )}
      </div>
    </div>
  );
};

export default ListData;
